import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import cn from 'classnames';
import { type Nullable } from '@/model/Common.model';
import styles from './Timepoint.module.scss';
import { _isEmptyString, _isNotBlank, _notNil } from '@/littledash';
import { DateUtils } from '@/utils/Date.utils';

type TimepointInputProps = {
  value: Nullable<string>;
  invalid?: boolean;
  disabled?: boolean;
  defaultUnit?: 'days' | 'hours' | 'minutes' | 'seconds';
  placeholder?: string;
  onChange: (value: { value: Nullable<string>; parseError: boolean }) => void;
  onBlur?: () => void;
  onParseFailure?: () => void;
};

export const TimepointInput: FC<TimepointInputProps> = ({
  value,
  defaultUnit = 'hours',
  placeholder = 'Enter timepoint (e.g., 15m, 1h, 7D)',
  onChange,
  onBlur,
  invalid = false,
}) => {
  const valueRef = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const onChangeRef = useRef(onChange);

  const clearInput = useCallback(() => {
    if (_notNil(inputRef.current)) {
      inputRef.current.value = '';
    }
  }, [inputRef]);

  const updateTimepointValue = useCallback(
    (value: Nullable<string>, parseError: boolean = false) => {
      if (valueRef.current !== value) {
        onChangeRef.current({ value, parseError });
      }
    },
    [valueRef, onChangeRef]
  );

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const setTimepointFromInput = useCallback(async (): Promise<boolean> => {
    if (_notNil(inputRef.current)) {
      const inputValue = (inputRef.current.value ?? '').trim();

      if (_isNotBlank(inputValue) && inputValue !== '-') {
        return DateUtils.parseDuration(inputValue, { defaultUnit })
          .then((newTimepoint) => {
            updateTimepointValue(newTimepoint);
            clearInput();
            return true;
          })
          .catch(() => {
            updateTimepointValue(inputValue, true);
            clearInput();
            return false;
          });
      }

      // Empty input handling
      updateTimepointValue('');
      clearInput();
      return Promise.resolve(true);
    }
    return Promise.resolve(false);
  }, [inputRef, defaultUnit, updateTimepointValue, clearInput]);

  useEffect(() => {
    const controller = new AbortController();
    if (_notNil(inputRef.current)) {
      inputRef.current.addEventListener(
        'focus',
        () => {
          if (_notNil(inputRef.current) && _notNil(valueRef.current) && _isEmptyString(inputRef.current.value)) {
            inputRef.current.value = DateUtils.renderDuration(valueRef.current, {
              defaultResponse: valueRef.current ?? '',
              format: 'short',
            });
            inputRef.current.select();
          }
        },
        { signal: controller.signal }
      );

      inputRef.current.addEventListener(
        'blur',
        () => {
          setTimepointFromInput().finally(() => {
            if (typeof onBlur === 'function') {
              onBlur();
            }
          });
        },
        { signal: controller.signal }
      );
      inputRef.current.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter') {
            event.stopImmediatePropagation();
            event.preventDefault();
            inputRef.current?.blur();
          }
        },
        { signal: controller.signal }
      );
    }
    return () => controller.abort();
  }, [inputRef, valueRef, setTimepointFromInput, onBlur]);
  const timepoint = useMemo(
    () => DateUtils.renderDuration(value, { defaultResponse: value ?? '', format: 'short', zeros: true }),
    [value]
  );
  return (
    <div
      className={cn('flex items-center ba br2 w-100 bg-white', styles.InputWrapper, {
        [styles.Error]: invalid,
      })}
      data-test-component="TimepointInput"
      data-test-element="container"
    >
      {_isNotBlank(timepoint) ? (
        <div
          className={cn('flex items-center br2 ph2 mr2 flex-shrink-0 f6', styles.Chip)}
          data-test-element="chip"
          onClick={() => inputRef.current?.focus()}
        >
          <span data-test-element="chip-value">{timepoint}</span>
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="text"
        data-test-element="timepoint-input"
        className="flex-grow-1 bn outline-0 f6 bg-transparent"
        placeholder={_isEmptyString(timepoint) ? placeholder : undefined}
      />
    </div>
  );
};
